import React, { useState, useMemo, useContext } from 'react';
import { IoMdClose, IoMdSearch } from 'react-icons/io';
import { HiOutlineDocumentDownload } from 'react-icons/hi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MainContext from '../../Contexts/MainContext';
import { GetAllHorarios } from '../../Services/ApiCalls';
import './styles.scss';

interface ModalPesquisaProps {
    pontos: any[];
    colaboradores: any[];
    onClose: () => void;
}

const timeToMinutes = (timeStr: string) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

const minutesToTime = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
};

const ModalPesquisa: React.FC<ModalPesquisaProps> = ({ pontos, colaboradores, onClose }) => {
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [colaboradorId, setColaboradorId] = useState<string>('todos');
    const [statusFilter, setStatusFilter] = useState<string>('todos');

    const { token } = useContext(MainContext);
    const { dataAllHorarios } = GetAllHorarios(token);

    const resultados = useMemo(() => {
        let filtrados = pontos;

        if (dataInicio) {
            const di = new Date(dataInicio + 'T00:00:00');
            filtrados = filtrados.filter(p => {
                const parts = p.data.split('/');
                const pDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                return pDate >= di;
            });
        }

        if (dataFim) {
            const df = new Date(dataFim + 'T23:59:59');
            filtrados = filtrados.filter(p => {
                const parts = p.data.split('/');
                const pDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                return pDate <= df;
            });
        }

        if (colaboradorId !== 'todos') {
            filtrados = filtrados.filter(p => Number(p.colaboradorId) === Number(colaboradorId));
        }

        if (statusFilter !== 'todos') {
            filtrados = filtrados.filter(p => (p.status || '').toUpperCase() === statusFilter);
        }

        const reports: Record<string, any> = {};

        filtrados.forEach(p => {
            const key = p.colaboradorId + '-' + p.data;
            if (!reports[key]) {
                const colab = colaboradores.find(c => Number(c.id) === Number(p.colaboradorId));
                reports[key] = {
                    colabName: colab?.nome || ('Colab #' + p.colaboradorId),
                    data: p.data,
                    entries: [],
                    exits: [],
                    statusCount: { aprovado: 0, reprovado: 0, pendente: 0 }
                };
            }
            if (p.tipo === 'ENTRADA') { reports[key].entries.push(timeToMinutes(p.hora)); }
            if (p.tipo === 'SAIDA') { reports[key].exits.push(timeToMinutes(p.hora)); }

            const st = (p.status || '').toUpperCase();
            if (st === 'APROVADO') { reports[key].statusCount.aprovado++; }
            else if (st === 'REJEITADO') { reports[key].statusCount.reprovado++; }
            else { reports[key].statusCount.pendente++; }
        });

        const reportList = Object.values(reports);
        reportList.sort((a: any, b: any) => {
            const partsA = a.data.split('/');
            const partsB = b.data.split('/');
            const da = new Date(Number(partsA[2]), Number(partsA[1]) - 1, Number(partsA[0]));
            const db = new Date(Number(partsB[2]), Number(partsB[1]) - 1, Number(partsB[0]));
            return da.getTime() - db.getTime();
        });

        reportList.forEach((r: any) => {
            r.entries.sort((a: number, b: number) => a - b);
            r.exits.sort((a: number, b: number) => a - b);

            let workedMins = 0;
            for (let i = 0; i < Math.min(r.entries.length, r.exits.length); i++) {
                workedMins += Math.max(0, r.exits[i] - r.entries[i]);
            }

            let JORNADA_NORMAL_MINUTES = 480;
            const colabObj = colaboradores.find(c => c.nome === r.colabName);
            if (colabObj && dataAllHorarios) {
                const horario = dataAllHorarios.find((h: any) => h.id === colabObj.horarioId)
                    || dataAllHorarios.find((h: any) => h.padrao);
                if (horario) {
                    const parts = r.data.split('/');
                    const dLocal = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                    const daysArr = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
                    const dayOfWeekStr = daysArr[dLocal.getDay()];
                    const diaRegra = horario.dias?.find((dRule: any) => dRule.diaDaSemana === dayOfWeekStr);
                    if (diaRegra && diaRegra.ativo) {
                        const inM = timeToMinutes(diaRegra.entrada);
                        const outM = timeToMinutes(diaRegra.saida);
                        const breakIn = timeToMinutes(diaRegra.inicioIntervalo);
                        const breakOut = timeToMinutes(diaRegra.fimIntervalo);
                        const workedExpected = Math.max(0, (outM - inM) - Math.max(0, breakOut - breakIn));
                        if (workedExpected > 0) { JORNADA_NORMAL_MINUTES = workedExpected; }
                    } else if (diaRegra && !diaRegra.ativo) {
                        JORNADA_NORMAL_MINUTES = 0;
                    }
                }
            }

            r.workedMins = workedMins;
            r.expectedMins = JORNADA_NORMAL_MINUTES;
            r.horasNormais = Math.min(workedMins, JORNADA_NORMAL_MINUTES);
            r.horasExtras = Math.max(0, workedMins - JORNADA_NORMAL_MINUTES);
        });

        return reportList;
    }, [pontos, dataInicio, dataFim, colaboradorId, statusFilter, colaboradores]);

    const totais = useMemo(() => {
        let normais = 0;
        let extras = 0;
        resultados.forEach(r => {
            normais += r.horasNormais;
            extras += r.horasExtras;
        });
        return { normais, extras };
    }, [resultados]);

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        const colorPrimary: [number, number, number] = [30, 41, 59];
        const colorHeader: [number, number, number] = [248, 250, 252];
        const colorOrange: [number, number, number] = [217, 119, 6];
        const colorBorder: [number, number, number] = [226, 232, 240];
        const colorSubtext: [number, number, number] = [100, 116, 139];

        const pageWidth = doc.internal.pageSize.getWidth();

        // Cabeçalho escuro
        doc.setFillColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
        doc.rect(0, 0, pageWidth, 28, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Relatorio de Jornada', 14, 12);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        const subtitleParts: string[] = [];

        if (dataInicio || dataFim) {
            const diStr = dataInicio ? new Date(dataInicio + 'T00:00:00').toLocaleDateString('pt-BR') : '--';
            const dfStr = dataFim ? new Date(dataFim + 'T00:00:00').toLocaleDateString('pt-BR') : '--';
            subtitleParts.push('Periodo: ' + diStr + ' a ' + dfStr);
        } else {
            subtitleParts.push('Periodo: Todos os registros');
        }

        if (colaboradorId !== 'todos') {
            const colabNome = colaboradores.find(c => String(c.id) === String(colaboradorId))?.nome || '';
            if (colabNome) { subtitleParts.push('Colaborador: ' + colabNome); }
        } else {
            subtitleParts.push('Colaborador: Todos');
        }

        const statusLabel: Record<string, string> = {
            todos: 'Todos',
            PENDENTE: 'Em Analise',
            APROVADO: 'Aprovados',
            REJEITADO: 'Reprovados'
        };
        subtitleParts.push('Status: ' + (statusLabel[statusFilter] || 'Todos'));

        doc.text(subtitleParts.join('   |   '), 14, 20);
        doc.text('Gerado em: ' + new Date().toLocaleString('pt-BR'), pageWidth - 14, 20, { align: 'right' });

        // Tabela
        const tableColumns = [
            { header: 'Colaborador', dataKey: 'colabName' },
            { header: 'Data', dataKey: 'data' },
            { header: 'Previsto', dataKey: 'previsto' },
            { header: 'Realizado', dataKey: 'realizado' },
            { header: 'H. Normal', dataKey: 'horasNormais' },
            { header: 'H. Extra', dataKey: 'horasExtras' },
            { header: 'Aprovacoes', dataKey: 'aprovacoes' },
        ];

        const tableRows = resultados.map(r => ({
            colabName: r.colabName,
            data: r.data,
            previsto: minutesToTime(r.expectedMins),
            realizado: minutesToTime(r.workedMins),
            horasNormais: minutesToTime(r.horasNormais),
            horasExtras: minutesToTime(r.horasExtras),
            aprovacoes: [
                r.statusCount.aprovado > 0 ? (r.statusCount.aprovado + ' Aprov.') : '',
                r.statusCount.pendente > 0 ? (r.statusCount.pendente + ' Pend.') : '',
                r.statusCount.reprovado > 0 ? (r.statusCount.reprovado + ' Repr.') : '',
            ].filter(Boolean).join(' '),
            _horasExtrasRaw: r.horasExtras,
        }));

        autoTable(doc, {
            startY: 34,
            columns: tableColumns,
            body: tableRows,
            theme: 'grid',
            headStyles: {
                fillColor: colorHeader,
                textColor: colorPrimary,
                fontStyle: 'bold',
                fontSize: 9,
                lineColor: colorBorder,
                lineWidth: 0.3,
            },
            bodyStyles: {
                fontSize: 8.5,
                textColor: [51, 65, 85] as [number, number, number],
                lineColor: colorBorder,
                lineWidth: 0.2,
            },
            alternateRowStyles: {
                fillColor: [249, 250, 251] as [number, number, number],
            },
            columnStyles: {
                colabName: { cellWidth: 50 },
                data: { cellWidth: 28 },
                previsto: { cellWidth: 24, halign: 'center' },
                realizado: { cellWidth: 24, halign: 'center' },
                horasNormais: { cellWidth: 26, halign: 'center' },
                horasExtras: { cellWidth: 24, halign: 'center' },
                aprovacoes: { cellWidth: 'auto', halign: 'center' },
            },
            didParseCell: (data) => {
                if (data.column.dataKey === 'horasExtras' && data.section === 'body') {
                    const row = data.row.raw as any;
                    if (row._horasExtrasRaw > 0) {
                        data.cell.styles.textColor = colorOrange;
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            },
            margin: { left: 14, right: 14 },
        });

        // Rodapé totais
        const finalY = (doc as any).lastAutoTable.finalY + 8;

        doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
        doc.setLineWidth(0.5);
        doc.line(14, finalY - 2, pageWidth - 14, finalY - 2);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colorSubtext[0], colorSubtext[1], colorSubtext[2]);
        doc.text('Total Horas Normais:', pageWidth - 120, finalY + 5);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
        doc.text(minutesToTime(totais.normais), pageWidth - 80, finalY + 5);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(colorSubtext[0], colorSubtext[1], colorSubtext[2]);
        doc.text('Total Horas Extras:', pageWidth - 55, finalY + 5);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colorOrange[0], colorOrange[1], colorOrange[2]);
        doc.text(minutesToTime(totais.extras), pageWidth - 14, finalY + 5, { align: 'right' });

        // Paginação
        const totalPages = (doc.internal as any).getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colorSubtext[0], colorSubtext[1], colorSubtext[2]);
            doc.text(
                'Pagina ' + i + ' de ' + totalPages + '   -   MarcaPonto - Relatorio de Jornada',
                pageWidth / 2,
                doc.internal.pageSize.getHeight() - 6,
                { align: 'center' }
            );
        }

        // Nome do arquivo
        const nomeColab = colaboradorId !== 'todos'
            ? (colaboradores.find(c => String(c.id) === String(colaboradorId))?.nome?.replace(/\s+/g, '_') || 'Todos')
            : 'Todos';
        const diFile = dataInicio ? dataInicio.replace(/-/g, '') : 'inicio';
        const dfFile = dataFim ? dataFim.replace(/-/g, '') : 'fim';
        doc.save('Relatorio_Jornada_' + nomeColab + '_' + diFile + '_' + dfFile + '.pdf');
    };

    return (
        <div className="modal-pesquisa__overlay" onClick={onClose}>
            <div className="modal-pesquisa__container" onClick={e => e.stopPropagation()}>
                <div className="modal-pesquisa__header">
                    <div>
                        <h2>Relatório Avançado</h2>
                        <p>Pesquisa estruturada de jornada e aprovações</p>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn-export-pdf"
                            onClick={handleExportPDF}
                            disabled={resultados.length === 0}
                            title="Exportar para PDF"
                        >
                            <HiOutlineDocumentDownload size={17} />
                            <span>Exportar PDF</span>
                        </button>
                        <button className="btn-close" onClick={onClose}><IoMdClose size={22} /></button>
                    </div>
                </div>

                <div className="modal-pesquisa__filters">
                    <div className="filter-group">
                        <label>Data Início</label>
                        <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
                    </div>
                    <div className="filter-group">
                        <label>Data Término</label>
                        <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
                    </div>
                    <div className="filter-group">
                        <label>Colaborador</label>
                        <select value={colaboradorId} onChange={e => setColaboradorId(e.target.value)}>
                            <option value="todos">Todos os Colaboradores</option>
                            {colaboradores.map(c => (
                                <option key={c.id} value={c.id}>{c.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Status Predominante</label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="todos">Todos</option>
                            <option value="PENDENTE">Em Análise (Pendente)</option>
                            <option value="APROVADO">Aprovados</option>
                            <option value="REJEITADO">Reprovados</option>
                        </select>
                    </div>
                </div>

                <div className="modal-pesquisa__body">
                    {resultados.length === 0 ? (
                        <div className="empty-state">
                            <IoMdSearch size={40} color="#cbd5e1" />
                            <p>Nenhum resultado encontrado para os filtros atuais.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="pesquisa-table">
                                <thead>
                                    <tr>
                                        <th>Colaborador</th>
                                        <th>Data</th>
                                        <th>Previsto</th>
                                        <th>Realizado</th>
                                        <th>H. Normal</th>
                                        <th>H. Extra</th>
                                        <th>Aprovações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultados.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.colabName}</td>
                                            <td>{r.data}</td>
                                            <td>{minutesToTime(r.expectedMins)}</td>
                                            <td>{minutesToTime(r.workedMins)}</td>
                                            <td>{minutesToTime(r.horasNormais)}</td>
                                            <td className={r.horasExtras > 0 ? "text-orange" : ""}>
                                                {minutesToTime(r.horasExtras)}
                                            </td>
                                            <td>
                                                <div className="mini-badges">
                                                    {r.statusCount.aprovado > 0 && <span className="m-badge m-ok">{r.statusCount.aprovado}</span>}
                                                    {r.statusCount.pendente > 0 && <span className="m-badge m-pend">{r.statusCount.pendente}</span>}
                                                    {r.statusCount.reprovado > 0 && <span className="m-badge m-err">{r.statusCount.reprovado}</span>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="modal-pesquisa__footer">
                    <div className="footer-summary">
                        <div className="summary-item">
                            <span>Total Horas Normais:</span>
                            <strong>{minutesToTime(totais.normais)}</strong>
                        </div>
                        <div className="summary-item extra">
                            <span>Total Horas Extras:</span>
                            <strong>{minutesToTime(totais.extras)}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalPesquisa;
